import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Button, Input, Select, Spin, message as antdMessage } from "antd";
import {
  ArrowsClockwise,
  ChatCircleText,
  CheckCircle,
  WarningCircle,
} from "@phosphor-icons/react";
import {
  fetchInboxMessages,
  markInboxMessageReadThunk,
  selectInboxMessageMutationStatus,
  selectInboxMessagesForDocument,
} from "../../slice/InboxSlice";
import { getUserId } from "../../utils/fetchLocalStorage";
import {
  getInboxMessageThread,
  listInboxMessageRecipients,
  markInboxMessageThreadRead,
  replyToInboxMessageThread,
} from "../../service/inbox";
import {
  BriefingSkeletonBlock,
  BriefingSkeletonGroup,
  InboxItemActionButton,
  InboxItemActionGroup,
  InboxItemBody,
  InboxItemFooterRow,
  InboxItemMeta,
  InboxItemMetaPerson,
  InboxItemRow,
  InboxItemUnreadDot,
  InboxListBody,
  InboxListEmptyIconRing,
  InboxListEmptyState,
  InboxListErrorBanner,
  InboxThreadParticipants,
} from "./AIPaneStyled";

/**
 * Phase 5c — per-document Messages panel.
 *
 * Lazy-fetches `GET /api/inbox/items/<docId>/messages/` on first
 * activation. Each row shows the body, sender + relative timestamp,
 * and a Mark-read affordance for unread messages where the current
 * user is the recipient.
 *
 * Per-row layout:
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ <body markdown-free text>                                │
 *   │                                                          │
 *   │ • alice → you · 2h ago                      [Mark read]  │
 *   └──────────────────────────────────────────────────────────┘
 *
 * A thread view (`threadId` set) additionally shows a "With: ..."
 * participants line above the messages and an "Add people" control
 * next to the reply box, since a conversation can now have more than
 * two members (cc at compose time, or looped in mid-conversation).
 *
 * Auto-mark-on-view is intentionally not implemented in v1: an
 * explicit Mark-read button gives the user agency over the read-
 * state side-effect (the BE notifies the sender via Notification
 * row, so silently auto-marking would notify the sender every time
 * the recipient opens the panel). We can revisit if testers find
 * the explicit button friction-y.
 */

const formatRelative = (iso) => {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  const deltaMs = Date.now() - ts;
  if (deltaMs < 0) return new Date(ts).toLocaleString();
  const sec = Math.floor(deltaMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
};

const personLabel = (user, fallbackSelf, currentUserId) => {
  if (user?.id != null && currentUserId != null && user.id === currentUserId) {
    return fallbackSelf;
  }
  return (
    (user && (user.name || user.full_name || user.username || user.email)) ||
    (user?.id != null ? `User #${user.id}` : "Unknown")
  );
};

const MessageRow = ({ messageItem, documentId, currentUserId }) => {
  const dispatch = useDispatch();
  const itemStatus = useSelector((state) =>
    selectInboxMessageMutationStatus(state, messageItem?.id)
  );

  const isRecipient =
    currentUserId != null && messageItem?.recipient?.id === currentUserId;
  const isUnread = !messageItem?.read_at;
  const canMarkRead = isRecipient && isUnread;
  const busy = itemStatus === "loading";

  const handleMarkRead = useCallback(() => {
    if (!canMarkRead || busy || !messageItem?.id) return;
    dispatch(
      markInboxMessageReadThunk({ documentId, messageId: messageItem.id })
    )
      .unwrap()
      .catch((err) => {
        antdMessage.error(err?.message || "Couldn't mark message read.");
      });
  }, [busy, canMarkRead, dispatch, documentId, messageItem?.id]);

  return (
    <InboxItemRow $unread={canMarkRead}>
      <InboxItemBody>{messageItem?.body || ""}</InboxItemBody>
      <InboxItemFooterRow>
        <InboxItemMeta>
          {canMarkRead && (
            <InboxItemUnreadDot aria-label="Unread message" />
          )}
          <span>
            <InboxItemMetaPerson>
              {personLabel(messageItem?.sender, "You", currentUserId)}
            </InboxItemMetaPerson>
            {/* `recipient` on the wire is scoped to the requester: it's
                populated only when *you* have a delivery receipt for this
                message, so it's always "you" when present — a thread can
                now have more than one other person, so we can't name a
                single counterparty here the way a 2-person exchange could. */}
            {messageItem?.recipient != null && (
              <>
                {" → "}
                <InboxItemMetaPerson>you</InboxItemMetaPerson>
              </>
            )}
          </span>
          <span>·</span>
          <span>{formatRelative(messageItem?.created_at)}</span>
        </InboxItemMeta>
        {canMarkRead && (
          <InboxItemActionGroup>
            <InboxItemActionButton
              type="button"
              onClick={handleMarkRead}
              disabled={busy}
              aria-label="Mark message read"
            >
              <CheckCircle size={12} weight="bold" />
              {busy ? "Marking…" : "Mark read"}
            </InboxItemActionButton>
          </InboxItemActionGroup>
        )}
      </InboxItemFooterRow>
    </InboxItemRow>
  );
};

MessageRow.propTypes = {
  messageItem: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    body: PropTypes.string,
    created_at: PropTypes.string,
    read_at: PropTypes.string,
    sender: PropTypes.object,
    recipient: PropTypes.object,
  }),
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserId: PropTypes.number,
};

const MessagesTab = ({ documentId, isActive, threadId = null }) => {
  const dispatch = useDispatch();
  const slot = useSelector((state) =>
    selectInboxMessagesForDocument(state, documentId)
  );
  const currentUserId = useMemo(() => getUserId(), []);
  const [thread, setThread] = useState(null);
  const [threadStatus, setThreadStatus] = useState("idle");
  const [threadError, setThreadError] = useState(null);
  const [draft, setDraft] = useState("");
  const [isReplying, setIsReplying] = useState(false);
  const [addPeopleIds, setAddPeopleIds] = useState([]);
  const [roster, setRoster] = useState([]);
  const isThreadView = threadId != null;
  const loadedThreadIdRef = useRef(null);

  useEffect(() => {
    if (!isThreadView || !isActive) return;
    listInboxMessageRecipients()
      .then((response) => setRoster(response.data || []))
      .catch(() => setRoster([]));
  }, [isActive, isThreadView]);

  const participants = useMemo(() => thread?.participants || [], [thread]);
  const addPeopleOptions = useMemo(() => {
    const existingIds = new Set([
      currentUserId,
      ...participants.map((user) => user?.id),
    ]);
    return roster
      .filter((user) => user?.id != null && !existingIds.has(user.id))
      .map((user) => ({
        value: user.id,
        label: user.name || user.full_name || user.username || user.email || `User #${user.id}`,
      }));
  }, [currentUserId, participants, roster]);

  useEffect(() => {
    if (!isThreadView || !isActive) return;
    let cancelled = false;
    // Switching threads reuses this mounted component (same URL, just
    // location.state changes), so anything scoped to the previous
    // conversation - a draft reply, people queued to add - must not
    // survive into the next one. Only reset on an actual thread change,
    // not when this effect re-runs because the tab was reactivated (that
    // would wipe an in-progress draft on the same conversation).
    if (loadedThreadIdRef.current !== threadId) {
      setThread(null);
      setDraft("");
      setAddPeopleIds([]);
    }
    // Always clear a prior failure, thread-change or not - a reload
    // (manual retry or tab reactivation) that succeeds shouldn't leave
    // the old error banner up.
    setThreadError(null);
    loadedThreadIdRef.current = threadId;
    setThreadStatus("loading");
    getInboxMessageThread(threadId)
      .then(async (response) => {
        if (cancelled) return;
        setThread(response.data);
        setThreadStatus("succeeded");
        // Reading a conversation clears all incoming turns at once. This is
        // separate from legacy per-message read state for old deep links.
        await markInboxMessageThreadRead(threadId);
      })
      .catch((error) => {
        if (cancelled) return;
        setThreadError(error?.message || "Couldn't load this conversation.");
        setThreadStatus("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [isActive, isThreadView, threadId]);

  useEffect(() => {
    if (isThreadView) return;
    if (!isActive || documentId == null) return;
    if (slot.status !== "idle") return;
    dispatch(fetchInboxMessages({ documentId }));
  }, [dispatch, documentId, isActive, isThreadView, slot.status]);

  const handleManualRefresh = useCallback(() => {
    if (isThreadView) {
      setThreadStatus("loading");
      getInboxMessageThread(threadId)
        .then((response) => {
          setThread(response.data);
          setThreadError(null);
          setThreadStatus("succeeded");
        })
        .catch((error) => {
          setThreadError(error?.message || "Couldn't load this conversation.");
          setThreadStatus("failed");
        });
      return;
    }
    if (documentId == null) return;
    dispatch(fetchInboxMessages({ documentId }));
  }, [dispatch, documentId, isThreadView, threadId]);

  const handleReply = useCallback(async () => {
    if (!threadId || !draft.trim() || isReplying) return;
    setIsReplying(true);
    try {
      const response = await replyToInboxMessageThread(
        threadId,
        draft.trim(),
        addPeopleIds
      );
      setThread(response.data);
      setDraft("");
      setAddPeopleIds([]);
    } catch (error) {
      antdMessage.error(error?.message || "Couldn't send reply.");
    } finally {
      setIsReplying(false);
    }
  }, [addPeopleIds, draft, isReplying, threadId]);

  if (!isThreadView && documentId == null) {
    return (
      <InboxListEmptyState>
        Select a document from the inbox to see its messages.
      </InboxListEmptyState>
    );
  }

  const { status, items, error } = isThreadView
    ? {
        status: threadStatus,
        items: thread?.messages || [],
        error: threadError,
      }
    : slot;
  const isLoading = status === "loading" && items.length === 0;
  const hasItems = items.length > 0;

  return (
    <>
      {error && (
        <InboxListErrorBanner role="alert">
          <WarningCircle size={14} weight="fill" style={{ marginRight: 6 }} />
          {error}
        </InboxListErrorBanner>
      )}
      <InboxListBody>
        {isThreadView && participants.length > 0 && (
          <InboxThreadParticipants>
            With: <strong>{participants.map((user) => personLabel(user, "You", currentUserId)).join(", ")}</strong>
          </InboxThreadParticipants>
        )}
        {isLoading ? (
          <BriefingSkeletonGroup aria-label="Loading messages">
            <BriefingSkeletonBlock $h={64} />
            <BriefingSkeletonBlock $h={48} />
          </BriefingSkeletonGroup>
        ) : !hasItems ? (
          <InboxListEmptyState>
            <InboxListEmptyIconRing>
              <ChatCircleText size={28} weight="duotone" />
            </InboxListEmptyIconRing>
            <span>
              No messages yet for this document.
              <br />
              Use <strong>Send message</strong> below to start a thread.
            </span>
            {status === "failed" && (
              <InboxItemActionButton
                type="button"
                onClick={handleManualRefresh}
              >
                <ArrowsClockwise size={14} weight="bold" /> Try again
              </InboxItemActionButton>
            )}
          </InboxListEmptyState>
        ) : (
          <>
            {status === "loading" && (
              <Spin
                size="small"
                style={{ alignSelf: "center", marginBottom: 4 }}
              />
            )}
            {items.map((messageItem) => (
              <MessageRow
                key={messageItem.id}
                messageItem={messageItem}
                documentId={documentId}
                currentUserId={currentUserId}
              />
            ))}
            {isThreadView && (
              <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                {addPeopleOptions.length > 0 && (
                  <Select
                    mode="multiple"
                    value={addPeopleIds}
                    onChange={setAddPeopleIds}
                    options={addPeopleOptions}
                    placeholder="Add people to this conversation (optional)"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  />
                )}
                <Input.TextArea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="Reply to this conversation"
                  autoSize={{ minRows: 2, maxRows: 5 }}
                />
                <Button
                  type="primary"
                  onClick={handleReply}
                  disabled={!draft.trim()}
                  loading={isReplying}
                >
                  {addPeopleIds.length > 0 ? "Reply and add people" : "Reply"}
                </Button>
              </div>
            )}
          </>
        )}
      </InboxListBody>
    </>
  );
};

MessagesTab.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isActive: PropTypes.bool,
  threadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default MessagesTab;
